import { setProperties } from '@ember/object';
import { next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { APIData, Coredata, RequestError, Service } from 'webapp-types';
import { BillingFeature, ModelName, Scale } from 'webapp/enums/enums';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { deleteKeys, normalizeToNumberArray, omit } from 'webapp/utils/common';

import CustomMetricsPopupPromoComponent from 'webapp/components/custom-metrics/popup-promo';

import type { UserRecord } from 'webapp/models';
import DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import { Type } from 'webapp/models-custom/infolayer-item/general';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import type SpaceRecord from 'webapp/models/space';
import DataLoaderBase from 'webapp/data-loader/base';

import { Severity } from 'webapp/services/logger';
import type MetricMoverService from 'webapp/services/metric-mover';
import type { RSjqXHR } from 'webapp/services/request';
import { AppType as VersionCheckerAppType } from 'webapp/services/version-checker';

export default class DataLoaderMain extends DataLoaderBase {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('tour') declare tourService: Service.Tour;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('logger') declare loggerService: Service.Logger;
	@service('socket') declare socketService: Service.Socket;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('connect') declare connectService: Service.Connect;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('flow/account-missing-features-setup')
	declare flowAccountMissingFeaturesSetupService: Service.Flow.AccountMissingFeaturesSetup;
	@service('onboarding') declare onboardingService: Service.Onboarding;
	@service('flow/reverse-trial') declare flowReverseTrialService: Service.Flow.ReverseTrial;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('flow/white-labeling-paying-customers-promo')
	declare flowWhiteLabelingPayingCustomersPromoService: Service.Flow.WhiteLabelingPayingCustomersPromo;
	@service('flow/cancellation') declare flowCancellationService: Service.Flow.Cancellation;
	@service('flow/promotions')
	declare flowPromotionsService: Service.Flow.Promotions;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('automated-popup-queue')
	declare automatedPopupQueueService: Service.AutomatedPopupQueue;
	@service('product-updates') declare productUpdatesService: Service.ProductUpdates;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('intercom') declare intercomService: Service.Intercom;
	@service('navigation-main') declare navigationMainService: Service.NavigationMain;
	@service('popup') declare popupService: Service.Popup;
	@service('metric-mover') declare metricMoverService: MetricMoverService;

	private userId: number | null = null; // temporary, only while loading data
	private rootSpaceId: number | null = null;
	private rootSpaceLevel: number | null = null;
	private isMfaRequired = false;

	@tracked override initialRequestsDone = false;
	initialRequestsPromise: Promise<void>;

	constructor() {
		super();
		this.initialRequestsDone = false;
		this.initialRequestsPromise = new Promise((resolve) => {
			// keep pending until we resolve it with the fn
			this.resolveInitialRequestsPromiseFn = resolve;
		});
	}

	autoConnectUrl = ''; // auto connect flow: /data-manager/auto-connect/Shopify?param1=value1&param2=value2
	externalInstallUrl = ''; // external install flow: /data-manager/external-install/HubspotGroup?step=...&returnUrl=...

	loadGlobals(allData: Coredata.Main): void {
		this.userId = allData.userId;
		this.rootSpaceId = allData.rootSpaceId;
		this.rootSpaceLevel = allData.rootSpaceLevel;

		deleteKeys(allData, 'rootSpaceId', 'rootSpaceLevel');

		// if we have infoMessage, show it
		const infoMessage = allData.infoMessage;
		if (infoMessage) {
			schedule('afterRender', this, function () {
				const type = infoMessage.type || Type.OK;
				// each infolayer Type has a method of the same name
				this.infolayerService[type as Type](infoMessage.text);
			});
		}
		deleteKeys(allData, 'infoMessage');

		// create temporary user record (for current-user service)
		const tmpUser = this.mmUsersService.createNewModel({
			name: allData.userName,
			email: allData.userEmail,
			level: allData.userLevel,
			timezone: allData.userTimezone,
			created: allData.userCreated,
		});

		this.userSettingsService.loadUserSettings(allData.userSettingsValues || {});

		deleteKeys(allData, 'userSettingsValues');

		setProperties(this.currentUserService, {
			model: tmpUser,
			loggedInAsPowerUser: allData.loggedInAsPowerUser,
			isPowerUser: allData.isPowerUser,
			isPlatformAdmin: allData.isPlatformAdmin,
			isExistingUser: allData.isExistingUser,
			isExistingUserEmailSet: allData.isExistingUserEmailSet,
			allowTransferToSpaceId: allData.isPlatformAdmin,
			isOnboarded: allData.isOnboarded,
			userGuid: allData.userGuid,
			metricLibraryExtraInfoToken: allData.metricLibraryExtraInfoToken,
		});

		deleteKeys(
			allData,
			'userId',
			'userName',
			'userEmail',
			'userLevel',
			'userTimezone',
			'loggedInAsPowerUser',
			'isPowerUser',
			'isPlatformAdmin',
			'isOnboarded',
			'userGuid',
			'metricLibraryExtraInfoToken',
		);

		// save tokens
		this.currentUserService.userDataToken = allData.userDataToken;
		this.currentUserService.accessToken = allData.accessToken;
		this.currentUserService.accessTokenExpiry = allData.accessTokenExpiry;
		this.currentUserService.initRefreshingTokens();

		deleteKeys(allData, 'userDataToken', 'accessToken', 'accessTokenExpiry');

		// other data that we need to save as we can't get it anywhere else

		this.socketService.authPusherKey = allData.authPusherKey;
		this.socketService.authPusherCluster = allData.authPusherCluster;

		deleteKeys(allData, 'authPusherKey', 'authPusherCluster');

		this.intercomService.bootData = allData.intercom ?? null;

		deleteKeys(allData, 'intercom');

		this.requestService.apiUrl = allData.apiUrl;
		this.requestService.connectApiUrl = allData.connectUrl;
		this.requestService.authServiceUrl = allData.authServiceUrl;
		this.requestService.amaBackendUrl = allData.amaBackendUrl;
		this.requestService.debugParam = allData.debugParam;
		this.requestService.pusherEnvId = allData.pusherEnvId;
		this.requestService.pusherKey = allData.pusherKey;
		this.requestService.pusherCluster = allData.pusherCluster;
		this.requestService.datasetsBackendUrl = allData.datasetsBackendUrl;
		this.requestService.datasetsPusherKey = allData.datasetsPusherKey;
		this.requestService.datasetsPusherCluster = allData.datasetsPusherCluster;
		this.requestService.datasetsSchemaExplorerUrl = allData.datasetsSchemaExplorerUrl;
		this.requestService.aiBffUrl = allData.aiBffUrl;
		this.requestService.authApiUrl = allData.authApiUrl;
		this.requestService.authGoogleClientId = allData.authGoogleClientId;
		this.requestService.metricMoverCustomerUrl = allData.metricMoverCustomerUrl;
		this.requestService.websiteUrl = allData.websiteUrl;
		this.requestService.metricMoverEnabled = Boolean(allData.metricMoverEnabled);
		this.requestService.metricMoverEnabledSpaceIds = normalizeToNumberArray(
			allData.metricMoverEnabledSpaceIds,
		);

		deleteKeys(
			allData,
			'apiUrl',
			'connectUrl',
			'authServiceUrl',
			'amaBackendUrl',
			'debugParam',
			'pusherEnvId',
			'pusherKey',
			'pusherCluster',
			'datasetsBackendUrl',
			'datasetsPusherKey',
			'datasetsPusherCluster',
			'aiBffUrl',
			'authApiUrl',
			'metricMoverCustomerUrl',
			'websiteUrl',
			'metricMoverEnabled',
			'metricMoverEnabledSpaceIds',
		);

		this.tourService.tourVariables = allData.tourValues;

		deleteKeys(allData, 'tourValues');

		this.versionCheckerService.version = allData.version;
		this.versionCheckerService.appType = VersionCheckerAppType.GENERAL;

		deleteKeys(allData, 'version');

		this.accountService.accountServiceUrl = allData.accountServiceUrl;
		this.accountService.cookieAuthSID = allData.cookieAuthSID;

		deleteKeys(allData, 'accountServiceUrl');

		DateRange.setFormulas(allData);

		DateRange.createPredefinedRanges();
		DateRange.createPredefinedGranulations();
		DsCompare.createPredefinedCompares();

		deleteKeys(allData, 'cdrRanges', 'cdrGranulations', 'cdrCompares');

		MetricLimitations.parseBaseConstraints(allData.cdrLimitationsBase.ranges);

		deleteKeys(allData, 'cdrLimitationsBase');

		// we have local config available for board colors
		deleteKeys(allData, 'boardColors');

		// all the rest: constants
		const constants = allData;

		// exception for scale: change null to -1 (selects don't handle null value well)
		const autoScaleEntry = constants.scales.find((item) => item.value === Scale.AUTO_BE);
		if (autoScaleEntry) {
			autoScaleEntry.value = Scale.AUTO;
		}

		this.constantsService.update(constants);

		// analytics
		this.analyticsService.initializeFromEnv();

		// isMfaRequired setting
		this.isMfaRequired = !!allData.isMfaRequired;
		deleteKeys(allData, 'isMfaRequired');

		this.connectService.initialize();

		this.datasetEventsService.datasetsSchemaExplorerUrl = allData.datasetsSchemaExplorerUrl;
		deleteKeys(allData, 'datasetsSchemaExplorerUrl');
	}

	doInitialRequests(): Promise<void> {
		void this.loadData().then(() => {
			this.markInitialRequestsPromiseAsResolved();

			this.navigationService.checkUrlToOpenChat();
		});

		return Promise.resolve(); // non-blocking
	}

	@tracked isTemplateOnboarding = false;

	afterInitialRequests(initialRouteTo: string) {
		// redirect to onboarding
		if (!this.currentUserService.isOnboarded) {
			const isTemplateOnboarding = this.onboardingService.isEligibleForOnboarding;
			this.isTemplateOnboarding = isTemplateOnboarding;

			const isAutoConnectFlow = initialRouteTo.startsWith('datamanager.auto-connect');
			const isExternalInstallFlow = initialRouteTo.startsWith('datamanager.external-install');
			if (!isAutoConnectFlow && !isExternalInstallFlow) {
				void this.initialRequestsPromise.then(() => {
					this.flowReverseTrialService.autoRequestTrial();
				});
			}

			if (this.accountService.isEligibleForAccountTypeChoice) {
				this.accountService.trackSignup();

				if (isAutoConnectFlow) {
					// rememeber given URL data
					// we'll need it to redirect from account/type-choice back to autoconnect
					this.autoConnectUrl = window.location.pathname + window.location.search;
				}

				if (isExternalInstallFlow) {
					// rememeber given URL data
					// we'll need it to redirect from account/type-choice back to external-install
					this.externalInstallUrl = window.location.pathname + window.location.search;
				}

				this.onboardingService.startOnboarding();
				return Promise.resolve();
			} else if (isAutoConnectFlow) {
				// just mark as onboarded, we are staying on same URL anyways
				this.markAsOnboarded('autoConnect');
				return null;
			} else if (isExternalInstallFlow) {
				// just mark as onboarded, we are staying on same URL anyways
				this.markAsOnboarded('externalInstall');
				return null;
			} else {
				// flow for invited users
				// or user1 that finished the first step (AccountTypeChoice) but not connecting source
				return this.redirectToOnboarding();
			}
		} else {
			void this.initialRequestsPromise.then(() => {
				this.billingService.checkToAddBillingIssuePopup();
				this.accountService.checkToAddMfaSetupPopup(!!this.isMfaRequired);

				// Returns early so the popups below can't clash with the install's navigation;
				// they're checked again on the next load.
				if (this.onboardingService.marketplaceSkillSlugs.length) {
					void this.onboardingService
						.installMarketplaceSkills()
						.then(([firstInstalledSlug]) => {
							if (firstInstalledSlug) {
								this.onboardingService.openGenieWithMarketplaceSkillPrompt(
									firstInstalledSlug,
								);
							}
						});
					return;
				}

				const currentTs = moment().unix();

				this.flowTourOnboardingService.startTour(true);
				this.flowWhiteLabelingPayingCustomersPromoService.checkToOpenPopup();
				this.flowAccountMissingFeaturesSetupService.checkToOpenPopup();
				next(() => {
					// In next so closeAllPopups1 doesn't dismiss it on some routes (ex: data-manager/connected on loading route transition)
					this.flowPromotionsService.checkToOpenGrowth2024Popup();
				});

				const billingActiveUntil = this.billingService.activeUntil;

				if (
					this.currentUserService.isAdmin &&
					!this.accountService.isClient &&
					this.billingService.isActive &&
					this.billingService.isPayingWithCreditCard
				) {
					const paymentMethod = this.billingService.paymentMethod,
						cardExpirationTs = moment(
							`${paymentMethod?.expYear}${paymentMethod?.expMonth}`,
							'YYYYMM',
						)
							.add(1, 'month')
							.unix(),
						cardExpiringPopupShownTs =
							this.userSettingsService.cardExpiringPopupShownTimestamp,
						oneWeekOfSubscriptionRemainingTs = moment
							.unix(billingActiveUntil)
							.subtract(7, 'day')
							.unix(),
						twoWeeksOfSubscriptionRemainingTs = moment
							.unix(billingActiveUntil)
							.subtract(14, 'day')
							.unix();

					if (
						cardExpirationTs &&
						cardExpirationTs < billingActiveUntil &&
						((twoWeeksOfSubscriptionRemainingTs <= currentTs &&
							cardExpiringPopupShownTs <= twoWeeksOfSubscriptionRemainingTs) ||
							(oneWeekOfSubscriptionRemainingTs <= currentTs &&
								cardExpiringPopupShownTs <= oneWeekOfSubscriptionRemainingTs))
					) {
						this.billingService.openCardExpiringPopup();
					}
				}

				this.navigationMainService.addOnboardingNewPopupToAutomatedPopupQueue();
				this.aiChatService.addModalToAutomatedPopupQueue();
				this.productUpdatesService.addModalToAutomatedPopupQueue();
				this.metricMoverService.addLegacyDeletionPopupToAutomatedPopupQueue();

				const popup = this.popupService.createModal(CustomMetricsPopupPromoComponent, {
					popupClass: 'popup-type-metrics-promo',
				});
				this.automatedPopupQueueService.queuePopup({
					popup,
					routeWhitelist: ['metrics.*', 'metric-library.*'],
					requiredPromises: [this.mmMetricsService.requestMetrics()],
					checkAddConditions: () => {
						return !this.userSettingsService.hasSeenCustomMetricsPromoPopup;
					},
					checkStartConditions: () => {
						const generalConditions =
							// its tiles only add to Home
							this.currentUserService.hasHome &&
							this.mmSourcesService.sourceModels.some((source) => {
								return source.sourceType.hasBasicOrPredefinedMetrics;
							}) &&
							!this.mmScriptsService.savedRecords.length &&
							!this.mmCustomQueriesService.savedRecords.length &&
							this.mmMetricsService.hasAnyCustomAndCalculatedMetrics;

						if (!generalConditions) {
							return false;
						}

						const billingConditions =
							this.billingService.checkIfCan(BillingFeature.QB) &&
							this.billingService.checkIfCan(BillingFeature.SCRIPTS) &&
							!this.billingService.isExpiredTrial;

						if (!billingConditions) {
							return false;
						}

						const userModel = this.currentUserService.model,
							createdTimestamp = userModel?.created,
							isNextDayOnApp =
								!!createdTimestamp &&
								moment().diff(moment.unix(createdTimestamp), 'days') > 1;

						return this.billingService.isActiveTrial ? isNextDayOnApp : true;
					},
				});

				// temporarily turned off for everyone https://app.asana.com/0/188486158010832/1200492037021146/f
				// this.currentUserService.checkToOpenCustomerSurvey();
			});

			return Promise.resolve();
		}
	}

	private loadData(): Promise<void> {
		void this.appNotificationsService.getResolutionCenterMessages();
		void this.appNotificationsService.getConnectRequests();
		void this.appNotificationsService.getConnectSelections();
		void this.appNotificationsService.getSystemStatus();
		void this.appNotificationsService.getNpsFeedback();

		void this.accountService.requestGlobalVisualSettings();

		// add class for tablet/mobile
		switch (this.currentUserService.deviceString) {
			case 'tablet':
				$('body').addClass('tablet');
				break;
			case 'phone':
				$('body').addClass('mobile');
				break;
		}

		const dAllPromise = this.requestService
			.api<APIData.App.AllResponse>('GET', '/all', null, { maxTryCount: 3 })
			.then((data) => {
				// account progress defaults
				if (!data.account.accountProgress) {
					data.account.accountProgress = {
						stage: 1,
						stepsCompleted: [],
					};
				}

				// DUMMY DATA FOR BILLING TESTING
				// BILLINGINFO

				// Has 2021 plan, need to migrate to 2023 plan
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 1'];

				// Has 2021 plan, need to migrate to 2023 plan and has scheduled change
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 2'];

				// Has 2023 plan
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 3'];
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 4'];

				// free, can request trial
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 5'];

				// on trial
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 6'];

				// quarterly, simple, no schedules, wl addon
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 7'];

				// quarterly, simple, no schedules, wl addon, manual payment, periodEnd !== activeUntil
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 8'];

				// downgrade billing cycle case
				// oct27 1603756800 whitelabeling trial -> active, oct29 1603929600 priority_sync ends, nov1 1604188800 switches to monthly, nov5 1604534400 source removed, dec1 1606780800
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 9'];

				// 2023 plan, eligibleForMigration for 2024
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 10'];

				// 2026 agency starter monthly
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 11'];

				// 2026 standard free
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 12'];

				// agency growth quarterly
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 13'];

				// standard growth quarterly
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 14'];

				// new sep 2026 plan - core
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 15'];

				// new sep 2026 plan - agency
				// data.account.billingInfo = window.dbx_mock['GET /d/all - billingInfo 16'];

				// data.account.billingInfo.subscriptions[0].items[0].id = 'custom';

				// annual commitment test
				// prettier-ignore
				// data.account.billingInfo.commitment = { startDate: 1773792000, actionDate: 1801440000, until: 1805328000 };

				// FEATUREINFO
				// data.account.featureInfo = window.dbx_mock['GET /d/all - featureInfo'];
				// data.account.featureInfo.reports_advanced_slides = false;
				// data.account.featureInfo.reports_basic = true;
				// data.account.featureInfo.board_loops = false;
				// data.account.featureInfo.clients = { current: 2, max: 5 };

				this.accountService.update(data.account);

				const spaceId = data.account.id;
				this.loggerService.spaceId = spaceId;

				try {
					// set spaceId as a LiveSession custom variable
					if (window.__ls) {
						window.__ls('identify', {
							params: { user_id: this.userId, space_id: spaceId },
						});
					}
				} catch (_e) {
					// skip
				}

				this.accountService.isPredefinedBlocksSpace = !!data.account.isSuperTemplatesSpace;
				this.accountService.isPublictemplatesSpace = !!data.account.isPublicTemplatesSpace;

				// rest of teh dataz
				this.storeService.pushPayload(omit(data, 'account'));

				if (!this.userId) {
					this.navigationService.logout();
					return;
				}

				const activeUser = this.storeService.peekRecord(
					ModelName.USER,
					this.userId,
				) as UserRecord;

				if (!activeUser) {
					this.navigationService.logout();
					return;
				} else if (!activeUser.id) {
					this.loggerService.logError('No user after /d/all', {
						userId: this.userId,
						usersData: JSON.stringify(data.users),
					});
				}

				const currentSpaceRecord = this.storeService.peekRecord(
					ModelName.SPACE,
					String(spaceId),
				) as SpaceRecord;
				this.accountService.currentSpace =
					this.mmSpacesService.createModelFromRecord(currentSpaceRecord);

				const activeUserModel = this.mmUsersService.createModelFromRecord(activeUser);
				activeUserModel.directPropUpdate({
					lastSeen: moment().unix(),
				});

				this.currentUserService.model = activeUserModel;

				this.currentUserService.rootSpaceId = this.rootSpaceId;
				this.currentUserService.rootSpaceLevel = this.rootSpaceLevel;

				// start socket
				this.socketService.subscribeToAppEvents();
				this.socketService.subscribeToAuthEvents();
				this.socketService.subscribeToAiConnection();

				// load templates if this is a template space (for home wall markings)
				if (this.accountService.isPublictemplatesSpace) {
					void this.mmTemplatesService.requestBoardPublicTemplates();
					void this.mmTemplatesService.requestReportPublicTemplates();
					void this.mmTemplatesService.requestBoardLoopPublicTemplates();
					void this.mmTemplatesService.requestBoardAccountTemplates();
					void this.mmTemplatesService.requestReportAccountTemplates();
					void this.mmTemplatesService.requestBoardLoopAccountTemplates();
				}
			});

		// must not process most of the requests before source types are loaded
		const sourceTypesPromise = this.mmSourceTypesService.requestSourceTypes(
			() => dAllPromise,
			true,
		);

		const activePlanPromotionsPromise = this.flowPromotionsService.requestPromotions();

		const promises: Promise<unknown>[] = [];

		promises.push(dAllPromise);

		promises.push(sourceTypesPromise);

		promises.push(activePlanPromotionsPromise);

		promises.push(this.mmBoardsService.requestBoards(true));

		promises.push(this.requestService.storeFindAll(ModelName.SOURCE));

		promises.push(this.billingService.fetchBillingOptions());

		// promises.push(new Promise(function(){})); // forever unresolved - to test skeleton screens

		return new Promise((resolve, reject) => {
			void Promise.allSettled(promises).then((statusArray) => {
				// since this will fail on the first rejected promise, just check the first
				const failed = statusArray.find((item) => item.status === 'rejected');
				if (failed) {
					// most of the initial requests go through the request service, which rejects with a RequestError
					const reason = failed.reason as
						(Partial<RequestError> & { obj?: RSjqXHR }) | undefined;
					this.infolayerService.error(
						'We\'ve encountered an error loading your account. If the error persists, please <a href="mailto:help@databox.com">contact our support team</a>.',
					);
					const ignoreLogging =
						(typeof reason?.code === 'number' && [0, 401, 403].includes(reason.code)) ||
						this.requestService.isForceRelogging;
					if (!ignoreLogging) {
						this.loggerService.logError(
							'Initial request(s) failed',
							{
								code: reason?.code,
								url: reason?.obj?.originalRequest?.url,
								requestId: reason?.obj?.originalRequest?.requestId,
							},
							Severity.CRITICAL,
						);
					}
					reject();
				} else {
					this.billingService.validateBillingOptions();
					// billing is known from here on: a free or trial plan gets the Intercom launcher, a paid one only its messages
					this.intercomService.boot();

					resolve();
				}
			});
		});
	}

	private redirectToOnboarding(): Promise<void> {
		// should show the first board blue bubble after
		this.tourService.isEligibleForFirstBoardViewTour = true;

		// first wizard counter for modal.wizard-first (will be opened after second visit of boards)
		void this.userSettingsService.save(UserSettingsKey.SHOW_FIRST_WIZARD, 2);

		const accountHasConnectedSources = this.mmSourcesService.getHasConnectedSources();

		if (this.currentUserService.isOwner) {
			if (accountHasConnectedSources) {
				// this flow can happen when owner has not finished onboarding
				// but invited userN has already connected sources
				this.onboardingService.markCompleted();
				this.navigationService.transitionToRoute('index');
			} else {
				this.onboardingService.startOnboarding();
			}
		} else {
			// if no sources in account yet (owner has presumably not finished onboarding)
			// create the sample source so the account is not empty
			// this will also mean the owner will finish their onboarding (when next time in app - see accountHasConnectedSources condition some lines above)
			if (!accountHasConnectedSources) {
				void this.onboardingService.useExcelSampleFile({ skipRedirectAndDoneEvent: true });
			}
			this.onboardingService.startOnboarding();
		}

		return Promise.resolve();
	}

	markAsOnboarded(onboardingType = 'regular'): void {
		this.requestService
			.api('POST', '/onboarded')
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		void this.userSettingsService.save(UserSettingsKey.ONBOARDING_TYPE, onboardingType);
	}
}
